import {
  findHeaderColumns,
  readCell,
  readCellText
} from 'features/RateCodes/workbookCells'

const HEADER_SCAN_ROW_LIMIT = 10
const HOTEL_NAME_SCAN_COLUMN_LIMIT = 20

const CODE_HEADER = 'value'
const DESCRIPTION_HEADER = 'description'
const MAPPING_HEADER = 'mapping'

const HOTEL_NAME_MINIMUM_TOKEN_LENGTH = 3
const HOTEL_NAME_STOP_WORDS = [
  'inn',
  'hotel',
  'hotels',
  'suite',
  'suites',
  'resort',
  'the',
  'and',
  'by',
  'of'
]

/**
 * Finds the Lighthouse header row. The header is not on row 1 and the data does not start
 * on column A, so both are discovered rather than assumed.
 */
export function detectLighthouseSheet(workbook) {
  if (!workbook || !workbook.worksheets) {
    return null
  }

  for (const sheet of workbook.worksheets) {
    const lastRow = Math.min(sheet.rowCount || 0, HEADER_SCAN_ROW_LIMIT)

    for (let rowNumber = 1; rowNumber <= lastRow; rowNumber += 1) {
      const columnsByHeader = findHeaderColumns(sheet.getRow(rowNumber))
      const codeCol = columnsByHeader[CODE_HEADER]
      const mappingCol = columnsByHeader[MAPPING_HEADER]

      if (codeCol && mappingCol) {
        return {
          sheet,
          headerRow: rowNumber,
          codeCol,
          descriptionCol: columnsByHeader[DESCRIPTION_HEADER] || null,
          mappingCol
        }
      }
    }
  }

  return null
}

/**
 * Reads the hotel name Lighthouse writes above the header row, used to warn when the file
 * does not belong to the hotel currently selected in the dashboard.
 */
export function readHotelNameFromSheet(sheet, headerRow) {
  for (let rowNumber = 1; rowNumber < headerRow; rowNumber += 1) {
    const row = sheet.getRow(rowNumber)

    for (let column = 1; column <= HOTEL_NAME_SCAN_COLUMN_LIMIT; column += 1) {
      const text = readCell(row, column)

      if (text) {
        return text
      }
    }
  }

  return ''
}

function toHotelNameTokens(hotelName) {
  return readCellText(hotelName)
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(
      token =>
        token.length >= HOTEL_NAME_MINIMUM_TOKEN_LENGTH &&
        !HOTEL_NAME_STOP_WORDS.includes(token)
    )
}

/**
 * Lighthouse writes the property name its own way ("HAMPTON WENTZVILLE" against our
 * "Hampton Inn Wentzville"), so relatedness is judged on shared distinctive words rather
 * than equality. Returns true when we cannot tell, to keep the warning meaningful.
 */
export function hotelNamesLookRelated(hotelNameFromFile, selectedHotelName) {
  const fileTokens = toHotelNameTokens(hotelNameFromFile)
  const selectedTokens = toHotelNameTokens(selectedHotelName)

  if (fileTokens.length === 0 || selectedTokens.length === 0) {
    return true
  }

  return fileTokens.some(token => selectedTokens.includes(token))
}

/**
 * Reads a detected Lighthouse sheet into the rows the Data Processor categorizes. Nothing is
 * mapped here on purpose: Lighthouse's buckets are translated to our categories server side,
 * where the rules can use the hotel's brand and ask a model about the rows they cannot settle.
 */
export function readLighthouseRows(detection) {
  const { sheet, headerRow, codeCol, descriptionCol, mappingCol } = detection
  const rows = []

  sheet.eachRow((row, rowNumber) => {
    if (rowNumber <= headerRow) {
      return
    }

    const rateCode = readCell(row, codeCol)

    if (!rateCode) {
      return
    }

    rows.push({
      rateCode,
      description: readCell(row, descriptionCol),
      mapping: readCell(row, mappingCol),
      rowNumber
    })
  })

  return rows
}

export default readLighthouseRows
